
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { WarrantyManager, WorkflowManager } from '@/lib/businessLogic';

export interface WarrantyClaim {
  id: string;
  claimNo: string;
  productId: string;
  productName: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  invoiceNo: string;
  purchaseDate: string;
  warrantyExpiryDate: string;
  claimType: 'repair' | 'replacement' | 'refund';
  issueDescription: string;
  claimDate: string;
  status: 'submitted' | 'under_review' | 'approved' | 'rejected' | 'in_progress' | 'completed' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string;
  estimatedCost?: number;
  actualCost?: number;
  completionDate?: string;
  rejectionReason?: string;
  approvalWorkflowId?: string;
  attachments?: string[];
  notes?: string;
  createdBy: string;
  createdAt: string;
}

export interface RepairOrder {
  id: string;
  orderNo: string;
  claimId: string;
  productId: string;
  productName: string;
  customerId: string;
  customerName: string;
  repairType: 'warranty' | 'paid';
  issueDescription: string;
  repairDescription: string;
  estimatedDays: number;
  estimatedCost: number;
  actualCost?: number;
  status: 'pending' | 'in_progress' | 'quality_check' | 'completed' | 'delivered' | 'cancelled';
  assignedTechnician?: string;
  startDate?: string;
  completionDate?: string;
  deliveryDate?: string;
  qualityCheckBy?: string;
  qualityCheckDate?: string;
  qualityCheckNotes?: string;
  customerNotified: boolean;
  createdBy: string;
  createdAt: string;
}

export interface ReplacementOrder {
  id: string;
  orderNo: string;
  claimId: string;
  originalProductId: string;
  originalProductName: string;
  replacementProductId?: string;
  replacementProductName?: string;
  customerId: string;
  customerName: string;
  reason: string;
  status: 'pending' | 'approved' | 'product_selected' | 'ready_for_exchange' | 'completed' | 'cancelled';
  originalProductReceived: boolean;
  replacementProductDelivered: boolean;
  valueDifference?: number;
  additionalPaymentRequired?: number;
  refundAmount?: number;
  exchangeDate?: string;
  approvalWorkflowId?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
}

export const useWarranty = () => {
  const queryClient = useQueryClient();

  // Get warranty claims with filtering
  const useWarrantyClaims = (filters?: {
    status?: string;
    claimType?: string;
    priority?: string;
    dateFrom?: string;
    dateTo?: string;
    customerId?: string;
  }) => {
    return useQuery({
      queryKey: ['warranty-claims', filters],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.claimType) params.append('claimType', filters.claimType);
        if (filters?.priority) params.append('priority', filters.priority);
        if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
        if (filters?.dateTo) params.append('dateTo', filters.dateTo);
        if (filters?.customerId) params.append('customerId', filters.customerId);
        
        const response = await api.get(`/warranty/claims?${params.toString()}`);
        return response.data;
      },
    });
  };

  // Get single warranty claim
  const useWarrantyClaim = (id: string) => {
    return useQuery({
      queryKey: ['warranty-claim', id],
      queryFn: async () => {
        const response = await api.get(`/warranty/claims/${id}`);
        return response.data;
      },
      enabled: !!id,
    });
  };

  // Validate warranty before creating claim
  const useValidateWarranty = () => {
    return useMutation({
      mutationFn: async ({ invoiceNo, productId }: { invoiceNo: string; productId: string }) => {
        // Use business logic to validate warranty
        const warrantyValidation = WarrantyManager.validateWarranty(`${invoiceNo}-${productId}`);
        
        if (!warrantyValidation.isValid) {
          throw new Error(`Warranty validation failed: ${warrantyValidation.status}`);
        }

        return {
          isValid: true,
          daysRemaining: warrantyValidation.daysRemaining,
          status: warrantyValidation.status,
          message: `Warranty is valid with ${warrantyValidation.daysRemaining} days remaining`
        };
      },
    });
  };

  // Create warranty claim with validation
  const useCreateWarrantyClaim = () => {
    return useMutation({
      mutationFn: async (claimData: Partial<WarrantyClaim>) => {
        // Validate warranty first
        if (claimData.invoiceNo && claimData.productId) {
          const warrantyValidation = WarrantyManager.validateWarranty(
            `${claimData.invoiceNo}-${claimData.productId}`
          );
          
          if (!warrantyValidation.isValid) {
            throw new Error(`Cannot create claim: Warranty ${warrantyValidation.status}`);
          }
        }

        // Create approval workflow for high-value claims
        let approvalWorkflowId;
        if (claimData.estimatedCost && claimData.estimatedCost > 50000) {
          const workflow = WorkflowManager.createApprovalRequest(
            'warranty_claim',
            claimData,
            'current_user_id'
          );
          approvalWorkflowId = workflow.id;
        }

        const enhancedClaimData = {
          ...claimData,
          claimNo: `WCL-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          claimDate: new Date().toISOString(),
          status: approvalWorkflowId ? 'under_review' : 'submitted',
          priority: claimData.priority || 'medium',
          approvalWorkflowId,
          createdBy: 'current_user_id',
          createdAt: new Date().toISOString()
        };

        const response = await api.post('/warranty/claims', enhancedClaimData);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['warranty-claims'] });
        queryClient.invalidateQueries({ queryKey: ['warranty-stats'] });
      },
    });
  };

  // Update warranty claim status
  const useUpdateWarrantyClaim = () => {
    return useMutation({
      mutationFn: async ({ 
        id, 
        status, 
        notes, 
        assignedTo,
        rejectionReason 
      }: {
        id: string;
        status: WarrantyClaim['status'];
        notes?: string;
        assignedTo?: string;
        rejectionReason?: string;
      }) => {
        const updateData: any = {
          status,
          notes,
          assignedTo,
          rejectionReason
        };

        if (status === 'completed') {
          updateData.completionDate = new Date().toISOString();
        }

        const response = await api.put(`/warranty/claims/${id}`, updateData);
        return response.data;
      },
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries({ queryKey: ['warranty-claim', id] });
        queryClient.invalidateQueries({ queryKey: ['warranty-claims'] });
        queryClient.invalidateQueries({ queryKey: ['warranty-stats'] });
      },
    });
  };

  // Get repair orders
  const useRepairOrders = (filters?: { status?: string; claimId?: string }) => {
    return useQuery({
      queryKey: ['repair-orders', filters],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.claimId) params.append('claimId', filters.claimId);
        
        const response = await api.get(`/warranty/repairs?${params.toString()}`);
        return response.data;
      },
    });
  };

  // Create repair order
  const useCreateRepairOrder = () => {
    return useMutation({
      mutationFn: async (repairData: Partial<RepairOrder>) => {
        const enhancedRepairData = {
          ...repairData,
          orderNo: `RPR-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          status: 'pending',
          customerNotified: false,
          createdBy: 'current_user_id',
          createdAt: new Date().toISOString()
        };

        const response = await api.post('/warranty/repairs', enhancedRepairData);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['repair-orders'] });
      },
    });
  };

  // Update repair order status
  const useUpdateRepairOrder = () => {
    return useMutation({
      mutationFn: async ({ 
        id, 
        status, 
        actualCost,
        completionDate,
        qualityCheckNotes 
      }: {
        id: string;
        status: RepairOrder['status'];
        actualCost?: number;
        completionDate?: string;
        qualityCheckNotes?: string;
      }) => {
        const updateData: any = { status };
        
        if (actualCost) updateData.actualCost = actualCost;
        if (completionDate) updateData.completionDate = completionDate;
        if (qualityCheckNotes) updateData.qualityCheckNotes = qualityCheckNotes;
        
        if (status === 'quality_check') {
          updateData.qualityCheckBy = 'current_user_id';
          updateData.qualityCheckDate = new Date().toISOString();
        }

        const response = await api.put(`/warranty/repairs/${id}`, updateData);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['repair-orders'] });
      },
    });
  };

  // Get replacement orders
  const useReplacementOrders = (filters?: { status?: string; claimId?: string }) => {
    return useQuery({
      queryKey: ['replacement-orders', filters],
      queryFn: async () => {
        const params = new URLSearchParams();
        if (filters?.status) params.append('status', filters.status);
        if (filters?.claimId) params.append('claimId', filters.claimId);
        
        const response = await api.get(`/warranty/replacements?${params.toString()}`);
        return response.data;
      },
    });
  };

  // Create replacement order
  const useCreateReplacementOrder = () => {
    return useMutation({
      mutationFn: async (replacementData: Partial<ReplacementOrder>) => {
        // Create approval workflow for replacements
        const workflow = WorkflowManager.createApprovalRequest(
          'warranty_claim',
          replacementData,
          'current_user_id'
        );

        const enhancedReplacementData = {
          ...replacementData,
          orderNo: `REP-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
          status: 'pending',
          originalProductReceived: false,
          replacementProductDelivered: false,
          approvalWorkflowId: workflow.id,
          createdBy: 'current_user_id',
          createdAt: new Date().toISOString()
        };

        const response = await api.post('/warranty/replacements', enhancedReplacementData);
        return response.data;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['replacement-orders'] });
      },
    });
  };

  // Get warranty statistics
  const useWarrantyStats = () => {
    return useQuery({
      queryKey: ['warranty-stats'],
      queryFn: async () => {
        const response = await api.get('/warranty/stats');
        return response.data;
      },
    });
  };

  // Get warranty reports
  const useWarrantyReports = (type: 'claims' | 'repairs' | 'replacements', dateRange: { from: string; to: string }) => {
    return useQuery({
      queryKey: ['warranty-reports', type, dateRange],
      queryFn: async () => {
        const params = new URLSearchParams({
          type,
          dateFrom: dateRange.from,
          dateTo: dateRange.to
        });
        
        const response = await api.get(`/warranty/reports?${params.toString()}`);
        return response.data;
      },
    });
  };

  return {
    useWarrantyClaims,
    useWarrantyClaim,
    useValidateWarranty,
    useCreateWarrantyClaim,
    useUpdateWarrantyClaim,
    useRepairOrders,
    useCreateRepairOrder,
    useUpdateRepairOrder,
    useReplacementOrders,
    useCreateReplacementOrder,
    useWarrantyStats,
    useWarrantyReports
  };
};
